import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shared/card';
import { Button } from '@/components/shared/button';
import { OpportunityScore } from '@/types/opportunity';
import { Badge } from '@/components/shared/badge';
import { HelpCircle } from 'lucide-react';
import { InfoIconWithTooltip } from '@/components/shared/InfoIconWithTooltip';
import { loadOpportunityDefinitionsFromCSV } from '@/utils/csvLoader';

// Empty fallback definitions since CSV is the sole source of truth
const fallbackDefinitions: { [key: string]: { description: string; whyItMatters: string; productURL: string } } = {};

// Cache for loaded definitions
let definitionsCache: { [key: string]: { description: string; whyItMatters: string; productURL: string } } | null = null;
let isLoadingDefinitions = false;

// Function to load definitions with caching
const loadDefinitions = async (): Promise<{ [key: string]: { description: string; whyItMatters: string; productURL: string } }> => {
  if (definitionsCache) {
    return definitionsCache;
  }

  if (isLoadingDefinitions) {
    // Wait for the current loading to complete
    while (isLoadingDefinitions) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return definitionsCache || fallbackDefinitions;
  }

  isLoadingDefinitions = true;

  try {
    console.log('Loading opportunity definitions from CSV (sole source of truth)...');
    definitionsCache = await loadOpportunityDefinitionsFromCSV();
    console.log('Successfully loaded definitions from CSV');
  } catch (error) {
    console.log('Failed to load from CSV, using empty fallback definitions');
    definitionsCache = fallbackDefinitions;
  }

  isLoadingDefinitions = false;
  return definitionsCache;
};

// Gradient color mapping table based on actual quadrant colors used in the app
const GRADIENT_COLORS: Record<string, { start: string; end: string }> = {
  '#CA0079': { start: '#CA0079', end: '#A13EFF' },  // Top Right - Most Valuable + Most Feasible (Pink/Magenta)
  '#3B50A2': { start: '#3B50A2', end: '#7dd2e2' },  // Top Left - Most Valuable + Less Feasible (Blue)
  '#F1AB11': { start: '#F1AB11', end: '#FF8C87' },  // Bottom Left - Less Valuable + Less Feasible (Yellow/Gold)
  '#64748b': { start: '#64748b', end: '#A1EECF' },  // Bottom Right - Less Valuable + More Feasible (Slate Gray)
};

interface OpportunitySummaryCardProps {
  opportunity: OpportunityScore;
  rank: number;
  onOpenAboutScore?: () => void;
}

const OpportunitySummaryCard = ({ opportunity, rank, onOpenAboutScore }: OpportunitySummaryCardProps) => {
  const [description, setDescription] = useState<string>('Loading...');
  const [whyItMatters, setWhyItMatters] = useState<string>('');
  const [productURL, setProductURL] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadDescription = async () => {
      try {
        const definitions = await loadDefinitions();
        const loadedDefinition = definitions[opportunity.Name];
        if (loadedDefinition) {
          setDescription(loadedDefinition.description);
          setWhyItMatters(loadedDefinition.whyItMatters);
          setProductURL(loadedDefinition.productURL);
        } else {
          setDescription('Definition not available.');
          setWhyItMatters('');
          setProductURL('');
        }
      } catch (error) {
        console.error('Error loading description:', error);
        setDescription('Definition not available.');
        setWhyItMatters('');
        setProductURL('');
      } finally {
        setIsLoading(false);
      }
    };

    loadDescription();
  }, [opportunity.Name]);

  // Remove the shortDescription logic - we want to show full text

  const handleOpenAboutScore = () => {
    if (onOpenAboutScore) {
      onOpenAboutScore();
    }
  };

  return (
    <Card className="flex flex-col h-[530px]">
      <CardHeader>
        <div className="flex justify-between items-start">
          <Badge variant="secondary" className="font-medium text-sm text-[#3B50A2] bg-gray-200 rounded-full text-center py-1 px-4 font-geist-mono hover:bg-gray-200">RANK #{rank}</Badge>
          <InfoIconWithTooltip 
            tooltipText="Learn more about how this score is defined and interpreted."
            onClick={handleOpenAboutScore}
            className="text-[#3B50A2] hover:text-[#CA0079] transition-colors"
          />
        </div>
        <CardTitle 
          className="text-2xl font-bold pt-4 bg-gradient-to-r bg-clip-text align-middle text-center text-transparent h-[80px]" 
          style={{ 
            backgroundImage: `linear-gradient(to right, ${
              GRADIENT_COLORS[opportunity.quadrantColor]?.start || opportunity.quadrantColor
            }, ${
              GRADIENT_COLORS[opportunity.quadrantColor]?.end || `${opportunity.quadrantColor}80`
            })` 
          }}
        >
          {opportunity.Name}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-grow flex flex-col">
        <div className="flex-grow space-y-4">
          <div className="grid grid-cols-3 gap-4 text-center border-b border-gray-400 pb-4">
            <div className="flex flex-col items-center justify-center">
              <p className="text-2xl font-bold">{Math.round(opportunity.valueScore)}%</p>
              <p className="text-sm text-muted-foreground">Value</p>
            </div>
            <div className="flex flex-col items-center justify-center">
              <p className="text-2xl font-bold">{Math.round(opportunity.feasibilityScore)}%</p>
              <p className="text-sm text-muted-foreground">Feasibility</p>
            </div>
            <div className="flex flex-col items-center justify-center">
              <p className="text-2xl font-bold">{Math.round(opportunity.finalScore)}%</p>
              <p className="text-sm text-muted-foreground">Final Score</p>
            </div>
          </div>
          <div className="flex-grow space-y-3 min-h-0">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <Badge 
                  variant="secondary" 
                  className="font-medium text-sm text-[#3B50A2] bg-gray-200 rounded-full text-center py-1 px-4 font-geist-mono hover:bg-gray-200 w-16"
                >
                  <span className="inline-block text-center w-full">WHAT</span>
                </Badge>
              </div>
              <p className="text-muted-foreground text-base flex-grow">
                {isLoading ? 'Loading description...' : description}
              </p>
            </div>
            {!isLoading && whyItMatters && (
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <Badge 
                    variant="secondary" 
                    className="font-medium text-sm text-[#3B50A2] bg-gray-200 rounded-full text-center py-1 px-4 font-geist-mono hover:bg-gray-200 w-16"
                  >
                    <span className="inline-block text-center w-full">WHY</span>
                  </Badge>
                </div>
                <p className="text-muted-foreground text-sm italic flex-grow">
                  {whyItMatters}
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="mt-4">
          <Button 
            variant="default" 
            className="w-full rounded-full"
            onClick={() => {
              if (productURL) {
                window.open(productURL, '_blank', 'noopener,noreferrer');
              }
            }}
            disabled={!productURL}
          >
            Learn More
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default OpportunitySummaryCard;
